import { Formik, Form } from 'formik';
import isEmpty from 'lodash/isEmpty';
import { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import styled from 'styled-components';
import Bugsnag from '@bugsnag/js';

import { ADDRESS_DEFAULT_INITIAL_VALUES, CHINA_COUNTRY_CODE } from 'checkout/enums/address';
import { AddressFormBySchema } from 'checkout/components/AddressForm/AddressFormBySchema';
import { AddressFormChina } from 'checkout/components/AddressForm/AddressFormChina';
import { AddressTextField } from 'checkout/components/AddressForm/AddressTextField';
import { Button } from 'shared/components/Button';
import CountryDropdownField from 'checkout/components/AddressForm/CountryDropdownField';
import { FieldType } from 'checkout/enums/FieldType';
import { FormikInput } from 'shared/components/FormikInput';
import { getShippingAddressValidationSchema } from 'shared/utils/validations';
import { GuestEmailForm } from 'checkout/components/Account/GuestEmailForm';
import { IAddress } from 'checkout/types/Address';
import { ITabName } from 'checkout/types/Tab';
import { login } from 'store/slice/authSlice';
import { requestService } from 'shared/utils/services/requestService';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';
import { ShippingMethods } from 'checkout/components/Shipping/ShippingMethods';
import { updateRegionPreferences } from 'account/services/regionPreferences';
import { useCheckoutContext } from 'checkout/providers/CheckoutContextProvider';
import { useLocation } from 'shared/hooks/useLocation';
import { useStoreDispatch } from 'store/hooks/storeHooks';
import { useCountriesQuery } from 'store/api/countries';
import { t } from 'localization/utils/t';

const TP = 'checkout.components.Shipping.ShippingAddressForm';

export const ShippingAddressForm = ({
  consentModal,
  formRef,
  setConsentModal,
  setIsAddingAddress,
}) => {
  const router = useRouter();

  const [chinaDistrict, setChinaDistrict] = useState({ display: '', value: '', zipcode: '' });
  const [countriesList, setCountriesList] = useState([]);
  const [country, setCountry] = useState({ display: '', value: '' });
  const [isChinaSelected, setIsChinaSelected] = useState(false);
  const [isCnidCardChecked, setIsCnidCardChecked] = useState(true);
  const [isNewsletterSignUpChecked, setIsNewsletterSignUpChecked] = useState(false);
  const [isPostalCodeRequired, setIsPostalCodeRequired] = useState(true);
  const [isStateRequired, setIsStateRequired] = useState(true);
  const [state, setState] = useState({ display: '', value: '' });

  const { data } = useCountriesQuery();
  const countries = data?.countries || [];
  const { selectedCountryCode } = useLocation();
  const storeDispatch = useStoreDispatch();
  const {
    addresses,
    changeTab,
    connectAddressToOrder,
    createGuestOrderFailureDispatcher,
    createGuestOrderRequestDispatcher,
    createGuestOrderSuccessDispatcher,
    createShippingAddress,
    createShippingAddressWithoutOrder,
    isGuestCheckout,
    loading,
    order,
  } = useCheckoutContext();

  const shippingAddresses = Object.values(addresses).filter(
    (address) => address.addressType === 'shipping',
  );

  const handleCountryMenuItemClick = (menuItem, currentFormValues, setValues) => {
    const selectedCountry = countriesList.find((_country) => _country.value === menuItem?.value);

    setCountry(menuItem);
    setState({ display: '', value: '' });

    setIsPostalCodeRequired(true);

    setIsStateRequired(selectedCountry?.stateRequired ?? true);

    setValues({
      ...ADDRESS_DEFAULT_INITIAL_VALUES,
      name: currentFormValues.name,
      email: currentFormValues.email,
    });
  };

  const handleGuestAddressSubmit = async (addressFormInput, formikActions) => {
    if (isChinaSelected && isCnidCardChecked && !consentModal.consent) {
      setConsentModal({ display: true, consent: false });
      return;
    }

    createGuestOrderRequestDispatcher();

    let orderNumber = null;

    try {
      const guestUser = await requestService({
        url: '/web-api/v1/create_guest',
        method: 'post',
        data: { guestEmail: addressFormInput?.email, emailRegistration: isNewsletterSignUpChecked },
        withCredentials: true,
      });

      storeDispatch(login({ user: guestUser }));
    } catch (error) {
      createGuestOrderFailureDispatcher(error.message);

      Bugsnag.notify(error, (event) => {
        event.severity = 'error';
        event.context = 'checkout';
        event.addMetadata('checkout', { orderNumber });
      });
    }

    // BUY3-2820: solves price mismatch between buy bar and checkout for guest user
    try {
      await updateRegionPreferences(selectedCountryCode);
    } catch (error) {
      Bugsnag.notify(error, (event) => {
        event.context = 'Error setting region preference for guest user';
      });
    }

    const shippingAddress = await createShippingAddressWithoutOrder(
      { ...addressFormInput, ...(isChinaSelected && { postalCode: chinaDistrict?.zipcode }) },
      formikActions,
    );

    if (!shippingAddress) {
      setConsentModal({ display: false, consent: false });
      return;
    }

    try {
      const orderEndpoint = router?.query?.productId
        ? '/web-api/v1/orders'
        : '/web-api/v1/orders/create_for_product_template';

      const orderInfo = {
        boxCondition: router?.query?.boxCondition,
        expectedPriceCents: Number(router?.query?.expectedPriceCents) || null,
        instantShip: router?.query?.instantShip,
        productId: Number(router?.query?.productId) || null,
        productTemplateId: Number(router?.query?.productTemplateId) || null,
        size: router?.query?.size,
      };

      for (const key of Object.keys(orderInfo)) {
        if (!orderInfo[key]) {
          delete orderInfo[key];
        }
      }

      const guestOrder = await requestService({
        url: orderEndpoint,
        method: 'post',
        data: { order: orderInfo },
        withCredentials: true,
      });

      orderNumber = guestOrder.number;

      router.query.orderNumber = orderNumber;
      await router.push(router, null, { shallow: true });

      createGuestOrderSuccessDispatcher(guestOrder);
    } catch (error) {
      createGuestOrderFailureDispatcher(error.message);

      Bugsnag.notify(error, (event) => {
        event.severity = 'error';
        event.context = 'checkout';
        event.addMetadata('checkout', { orderNumber });
      });
    }

    await connectAddressToOrder(shippingAddress?.id, orderNumber);
  };

  const handleAddressSubmit = async (address: IAddress, formikActions) => {
    if (isChinaSelected && isCnidCardChecked && !consentModal.consent) {
      setConsentModal({ display: true, consent: false });
      return;
    }

    const shippingAddress = await createShippingAddress(
      { ...address, ...(isChinaSelected && { postalCode: chinaDistrict?.zipcode }) },
      formikActions,
    );

    if (!shippingAddress) {
      setConsentModal({ display: false, consent: false });
      return;
    }

    if (shippingAddress.countryCode === shippingAddresses[0]?.countryCode) {
      changeTab(ITabName.PAYMENT);

      sendTrackingEvent('CHECKOUT_ADDRESS_CONFIRMATION_TAP', {
        chinese_citizen: (country?.value === CHINA_COUNTRY_CODE).toString(),
        country: address.countryCode,
        event_context: 'shipping_address_form',
        shipping_method: order.shippingServiceLevel,
        state: address.state,
      });
    } else {
      setIsAddingAddress(false);
    }
  };

  const handleNewsletterSignUpChange = () => {
    setIsNewsletterSignUpChecked(!isNewsletterSignUpChecked);
  };

  const handleChinaCitizenIdCardChange = () => {
    setIsCnidCardChecked(!isCnidCardChecked);
  };

  const handleDisabledSubmitButtonClick = (errors) => {
    const errorKeys = Object.keys(errors);
    if (errorKeys.length > 0) {
      document.getElementsByName(errorKeys[0])?.[0]?.focus();
    }
  };

  useEffect(() => {
    if (!selectedCountryCode) return;

    ADDRESS_DEFAULT_INITIAL_VALUES.countryCode = selectedCountryCode;

    if (countries?.length > 0) {
      const shipsToCountry = countries.filter((_country) => _country.shipsTo);

      const transformedCountries = shipsToCountry.map((_country) => ({
        display: _country.name,
        value: _country.isoCode,
        zipRequired: _country.zipRequired,
        stateRequired: _country.stateRequired,
      }));

      setCountriesList(transformedCountries);

      const selectedCountry = transformedCountries.find(
        (_country) => _country.value === selectedCountryCode,
      );

      if (selectedCountry) {
        setCountry(selectedCountry);
      }
    }
  }, [countries?.length]);

  useEffect(() => {
    setIsChinaSelected(country?.value === CHINA_COUNTRY_CODE);
  }, [country?.value]);

  return (
    <Formik
      enableReinitialize
      initialValues={ADDRESS_DEFAULT_INITIAL_VALUES}
      innerRef={formRef}
      onSubmit={isGuestCheckout ? handleGuestAddressSubmit : handleAddressSubmit}
      validationSchema={getShippingAddressValidationSchema({
        countryIsoCode: country?.value,
        isChinaCitizenIdCard: isCnidCardChecked,
        isGuestCheckout,
        isPostalCodeRequired,
        isStateRequired,
      })}
    >
      {({ isSubmitting, isValid, dirty, values, setValues, errors }) => (
        <ShippingAddressForm.Form>
          {isGuestCheckout && (
            <GuestEmailForm
              isNewsletterSignUpChecked={isNewsletterSignUpChecked}
              handleNewsletterSignUpChange={handleNewsletterSignUpChange}
            />
          )}
          <AddressTextField
            type={FieldType.FullName}
            label={t(`${TP}.shippingFullNameLabel`, 'Full Name')}
            name="name"
            data-qa="checkout_shipping_full_name"
          />
          <ShippingAddressForm.CountryDropdownField
            countriesList={countriesList}
            onMenuItemClick={(menuItem) => {
              handleCountryMenuItemClick(menuItem, values, setValues);
            }}
            selectedCountry={country}
          />

          {!isChinaSelected && (
            <AddressFormBySchema
              country={country}
              isPostalCodeRequired={isPostalCodeRequired}
              onStateClick={(item) => setState(item)}
              state={state}
            />
          )}

          {isChinaSelected && (
            <AddressFormChina
              chinaDistrict={chinaDistrict}
              handleChinaCitizenIdCardChange={handleChinaCitizenIdCardChange}
              isCnidCardChecked={isCnidCardChecked}
              setChinaDistrict={setChinaDistrict}
            />
          )}

          {order?.number && <ShippingMethods />}

          <div style={{ position: 'relative', marginTop: '48px' }}>
            {!(isValid && dirty) && (
              <ShippingAddressForm.DisabledButton
                onClick={() => handleDisabledSubmitButtonClick(errors)}
              />
            )}
            <ShippingAddressForm.Button
              data-qa="checkout_shipping_submit"
              title={
                isGuestCheckout
                  ? t(`${TP}.saveAndContinue`, 'SAVE AND CONTINUE')
                  : t(`${TP}.continuePaymentButton`, 'Continue to Payment')
              }
              type="submit"
              loading={loading.address || isSubmitting}
              disabled={!(isValid && dirty) || isSubmitting}
            />
          </div>

          {!isEmpty(shippingAddresses) && (
            <ShippingAddressForm.Back
              href=""
              data-qa="checkout_shipping_back"
              onClick={(event) => {
                event.preventDefault();

                setIsAddingAddress(false);
              }}
            >
              {t(`${TP}.backToAddressButton`, 'Back to Saved Addresses')}
            </ShippingAddressForm.Back>
          )}
        </ShippingAddressForm.Form>
      )}
    </Formik>
  );
};

ShippingAddressForm.Form = styled(Form)`
  input,
  select {
    font-size: 11px;
  }
  margin-top: 56px;
  display: flex;
  flex-direction: column;

  @media (min-width: ${({ theme }) => theme.breakpoints.small}px) {
    input,
    select {
      font-size: 11.5px;
    }
  }
`;

ShippingAddressForm.FormikInput = styled(FormikInput)`
  margin-bottom: unset;
`;

ShippingAddressForm.CountryDropdownField = styled(CountryDropdownField)`
  padding-bottom: 24px;
  margin-top: -1px;

  div:nth-child(2) {
    div:nth-child(1) {
      padding-bottom: 8px;
    }
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.small}px) {
    padding-bottom: 24px;
  }
`;

ShippingAddressForm.Button = styled(Button)`
  font-size: 11px;
  width: 100%;
  text-transform: uppercase;
`;

ShippingAddressForm.DisabledButton = styled.div`
  position: absolute;
  width: 100%;
  height: 42px;
`;

ShippingAddressForm.Back = styled.a`
  display: block;
  text-align: center;
  margin-top: 24px;
  text-decoration: underline;
  align-self: center;

  @media (min-width: ${({ theme }) => theme.breakpoints.small}px) {
    width: 315px;
  }
`;
